import { DesktopWindowsOutlined, PhoneIphoneOutlined } from '@mui/icons-material'
import { Stack, Typography } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { EmployeeBox, FlexBox, GapBox } from 'components/Box'
import { BoxForm, NumberField, Select, Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { SingleAdViewTimeSoftValidator } from 'features/globalSetup/pages/Defaults/RichPlacements/Sections/MultiAnchorSection/SingleAdViewTimeSoftValidator'
import { adDisplaysBeforeRefreshValues } from 'features/globalSetup/types/adDisplaysBeforeRefresh'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'
import { useGetWorkspaceDefaults, WorkspaceDefaults } from './useGetWorkspaceDefaults'
import { BasicSettingsWorkspaceDefaults } from './WorkspaceDefaults/BasicSettingsWorkspaceDefaults'

import type { Schema } from './schema'
import type { SelectOption } from 'components/Select/types'
import type { AdDisplaysBeforeRefresh } from 'features/globalSetup/types/adDisplaysBeforeRefresh'

export const BasicSettingsSection = (): React.JSX.Element => {
  const ability = useAbility()
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('features/domain', {
    keyPrefix: 'form.richPlacements.multiAnchor.basicSettings',
  })

  const { control, watch } = useFormContext<Schema>()
  const { customizeBasicSettings, frequencyCappingEnabled } = watch()

  const cannotUpdate: boolean = ability.cannot('update', 'Domain')
  const canReadFrequencyCapping: boolean = ability.can('read', 'DomainField-frequencyCapping')
  const cannotUpdateFrequencyCapping: boolean = ability.cannot(
    'update',
    'DomainField-frequencyCapping',
  )

  const workspaceDefaults: undefined | WorkspaceDefaults = useGetWorkspaceDefaults()

  const DEVICE_CONTAINER_SPACING = 8 // [px]

  return (
    <Paper data-cy='section-basic-settings'>
      <SectionHeader
        subtitle={t('subtitle')}
        title={t('title')}
      />

      <GapBox />

      <BoxForm>
        <Switch
          control={control}
          disabled={cannotUpdate}
          label={t('customizeBasicSettings.label')}
          labelPlacement='start'
          name='customizeBasicSettings'
          tooltip={{ content: t('customizeBasicSettings.tooltip') }}
        />

        {customizeBasicSettings && (
          <>
            <Stack
              direction='row'
              spacing={DEVICE_CONTAINER_SPACING}
            >
              <BoxForm>
                <FlexBox
                  axis='x'
                  sx={{ gap: 1 }}
                >
                  <DesktopWindowsOutlined
                    data-cy='desktop-windows-outlined-icon'
                    fontSize='inherit'
                  />
                  <Typography>{tc('device.desktop')}</Typography>
                </FlexBox>

                <NumberField
                  control={control}
                  disabled={cannotUpdate}
                  helperText={
                    <SingleAdViewTimeSoftValidator<Schema> path='desktopSingleAdViewTime' />
                  }
                  label={t('columns.singleAdViewTime.label')}
                  name='desktopSingleAdViewTime'
                  tooltip={{ content: t('columns.singleAdViewTime.tooltip') }}
                  width='md'
                />

                <Select<Schema, AdDisplaysBeforeRefresh>
                  control={control}
                  disabled={cannotUpdate}
                  label={t('columns.adDisplaysBeforeRefresh.label')}
                  name='desktopAdDisplaysBeforeRefresh'
                  options={adDisplaysBeforeRefreshValues.map(
                    (value: AdDisplaysBeforeRefresh): SelectOption<AdDisplaysBeforeRefresh> => ({
                      label: value,
                      value,
                    }),
                  )}
                  tooltip={{ content: t('columns.adDisplaysBeforeRefresh.tooltip') }}
                />
              </BoxForm>

              <BoxForm>
                <FlexBox
                  axis='x'
                  sx={{ gap: 1 }}
                >
                  <PhoneIphoneOutlined
                    data-cy='phone-iphone-outlined-icon'
                    fontSize='inherit'
                  />
                  <Typography>{tc('device.mobile')}</Typography>
                </FlexBox>

                <NumberField
                  control={control}
                  disabled={cannotUpdate}
                  helperText={
                    <SingleAdViewTimeSoftValidator<Schema> path='mobileSingleAdViewTime' />
                  }
                  label={t('columns.singleAdViewTime.label')}
                  name='mobileSingleAdViewTime'
                  tooltip={{ content: t('columns.singleAdViewTime.tooltip') }}
                  width='md'
                />

                <Select<Schema, AdDisplaysBeforeRefresh>
                  control={control}
                  disabled={cannotUpdate}
                  label={t('columns.adDisplaysBeforeRefresh.label')}
                  name='mobileAdDisplaysBeforeRefresh'
                  options={adDisplaysBeforeRefreshValues.map(
                    (value: AdDisplaysBeforeRefresh): SelectOption<AdDisplaysBeforeRefresh> => ({
                      label: value,
                      value,
                    }),
                  )}
                  tooltip={{ content: t('columns.adDisplaysBeforeRefresh.tooltip') }}
                />
              </BoxForm>
            </Stack>

            <Switch
              control={control}
              disabled={cannotUpdate}
              label={t('displayAfterUserInteraction.label')}
              labelPlacement='start'
              name='displayAfterUserInteraction'
              tooltip={{ content: t('displayAfterUserInteraction.tooltip') }}
            />

            {customizeBasicSettings && workspaceDefaults && (
              <BasicSettingsWorkspaceDefaults workspaceDefaults={workspaceDefaults} />
            )}

            {canReadFrequencyCapping && (
              <EmployeeBox>
                <BoxForm>
                  <Switch
                    control={control}
                    disabled={cannotUpdate || cannotUpdateFrequencyCapping}
                    label={t('frequencyCapping.label')}
                    labelPlacement='start'
                    name='frequencyCappingEnabled'
                    tooltip={{ content: t('frequencyCapping.tooltip') }}
                  />

                  {frequencyCappingEnabled && (
                    <BoxForm
                      sx={{
                        ml: CONTAINER_SPACING,
                      }}
                    >
                      <NumberField
                        control={control}
                        disabled={cannotUpdate || cannotUpdateFrequencyCapping}
                        label={t('frequencyCapping.impressionLimit.label')}
                        name='impressionLimit'
                        tooltip={{
                          content: t('frequencyCapping.impressionLimit.tooltip'),
                        }}
                      />
                      <NumberField
                        control={control}
                        disabled={cannotUpdate || cannotUpdateFrequencyCapping}
                        label={t('frequencyCapping.timeWindow.label')}
                        name='timeWindow'
                        tooltip={{
                          content: t('frequencyCapping.timeWindow.tooltip'),
                        }}
                      />
                    </BoxForm>
                  )}
                </BoxForm>
              </EmployeeBox>
            )}
          </>
        )}
      </BoxForm>
    </Paper>
  )
}
